/**
 * The review panel's words, ported from the plugin's dock.
 *
 * Every function here mirrors one in `review_dock.py`, because a reviewer who
 * checks a deed in QGIS on Tuesday and in the browser on Wednesday must read
 * the same sentence about the same document. The rule the plugin encodes and
 * this keeps: **a document is reported in the unit it was written in.**
 * Handing a Dutch plan "0.494 ac" makes the reviewer redo a conversion the
 * server already did, and rounding a 200-square-foot easement to three
 * decimals of an acre throws away the only figure that could corroborate it.
 */

import type {
  BearingModel,
  Call,
  EasementDescription,
  ServerVerdict,
  Tract,
} from "./types";

const UNIT_LABELS: Record<string, string> = {
  feet: "ft",
  meters: "m",
  varas: "varas",
  chains: "chains",
};

export function unitLabel(unit: string | undefined): string {
  return UNIT_LABELS[unit ?? "feet"] ?? unit ?? "ft";
}

export function unitOf(tract: Tract): string {
  return tract.distance_unit || "feet";
}

function group(value: number, decimals = 0): string {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** The area the document stated, in the document's own unit. The order is
 *  the server's comparison order, so what shows is what was checked. */
export function statedAreaText(tract: Tract): string {
  if (tract.stated_area_sqm)
    return ` · stated ${group(tract.stated_area_sqm)} m²`;
  if (tract.stated_hectares) return ` · stated ${tract.stated_hectares} ha`;
  if (tract.stated_area_sqft)
    return ` · stated ${group(tract.stated_area_sqft)} sq ft`;
  if (tract.stated_acreage) return ` · stated ${tract.stated_acreage} ac`;
  return "";
}

/** The computed area, in the unit this document would state it in. Hectares
 *  take over above one, where square metres stop being readable. */
export function computedAreaText(tract: Tract, verdict: ServerVerdict): string {
  const {
    computed_sqm: sqm,
    computed_hectares: hectares,
    computed_sqft: sqft,
  } = verdict;
  const metricDocument = (verdict.distance_unit ?? "feet") === "meters";
  if ((tract.stated_area_sqm || metricDocument) && sqm) {
    if (hectares && hectares >= 1) return `computed ${group(hectares, 4)} ha`;
    return `computed ${group(sqm, 1)} m²`;
  }
  if (tract.stated_hectares && hectares)
    return `computed ${group(hectares, 4)} ha`;
  if (tract.stated_area_sqft && sqft) return `computed ${group(sqft)} sq ft`;
  const acres = verdict.geometry?.acres ?? verdict.computed_acres ?? 0;
  return `computed ${acres.toFixed(3)} ac`;
}

export function closureText(verdict: ServerVerdict): string {
  const ratio = verdict.closure_ratio;
  if (!ratio) return "";
  return ratio === "exact" ? "closes exactly" : `closure ${ratio}`;
}

/** A bearing as a surveyor writes it. Mirrors `dxf.bearing_text`. */
export function bearingText(bearing: BearingModel | null | undefined): string {
  if (!bearing) return "-";
  const seconds = Number(bearing.seconds ?? 0);
  const shown = Number.isInteger(seconds) ? `${seconds}` : seconds.toFixed(1);
  return `${bearing.ns} ${bearing.degrees}°${String(bearing.minutes).padStart(2, "0")}'${shown}" ${bearing.ew}`;
}

export function courseBearing(call: Call): string {
  return bearingText(
    call.call_type === "curve" ? call.chord_bearing : call.bearing,
  );
}

export function courseDistance(call: Call): string {
  const distance =
    call.call_type === "curve" ? call.chord_length : call.distance;
  return distance ? group(distance, 2) : "-";
}

export function curveText(call: Call, unit: string): string {
  if (call.call_type !== "curve") return "";
  const mark = unitLabel(unit);
  const parts: string[] = [];
  if (call.radius) parts.push(`R=${group(call.radius, 2)} ${mark}`);
  if (call.arc_length) parts.push(`L=${group(call.arc_length, 2)} ${mark}`);
  if (call.curve_direction) parts.push(call.curve_direction);
  return parts.join(", ") || "chord only";
}

export function referenceText(
  ref:
    | {
        book?: string | null;
        page?: string | null;
        instrument?: string | null;
      }
    | null
    | undefined,
): string {
  if (!ref) return "";
  const parts = [
    ref.book ? `Book ${ref.book}` : "",
    ref.page ? `Page ${ref.page}` : "",
    ref.instrument ? `Instrument ${ref.instrument}` : "",
  ].filter(Boolean);
  return parts.join(", ");
}

/** What the instrument burdens the land with, in the order a land agent
 *  reads it. */
export function easementLines(easement: EasementDescription): string[] {
  const bits: string[] = [];
  if (easement.easement_type)
    bits.push(`${title(easement.easement_type)} easement`);
  if (easement.width_ft) bits.push(`${easement.width_ft} ft wide`);
  if (easement.term) bits.push(easement.term);
  if (easement.exclusive !== null && easement.exclusive !== undefined) {
    bits.push(easement.exclusive ? "exclusive" : "non-exclusive");
  }
  const lines = bits.length ? [bits.join(" · ")] : [];
  if (easement.rights?.length)
    lines.push(`Rights: ${easement.rights.join(", ")}`);
  const burdens = referenceText(easement.servient_reference);
  if (burdens) lines.push(`Burdens: ${burdens}`);
  return lines.length ? lines : ["Easement"];
}

export function aliquotChain(parts: string[] | undefined): string {
  const names: Record<string, string> = {
    N2: "N½",
    S2: "S½",
    E2: "E½",
    W2: "W½",
    NE4: "NE¼",
    NW4: "NW¼",
    SE4: "SE¼",
    SW4: "SW¼",
  };
  return (parts ?? []).map((part) => names[part] ?? part).join(" of ");
}

function title(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** An anchor is the document's own monument text and can run long. */
export function elide(text: string, limit = 44): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;
}
